// Restringe el acceso a la app: solo la cuenta autorizada puede entrar.
// El resto de los visitantes ve "Plataforma en construcción".
import React, { useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  signOut,
} from "firebase/auth";
import { auth } from "./firebase";

const ALLOWED_EMAIL = "juli.dapur@gmail.com";

const isAllowed = (user) =>
  !!user &&
  (user.email || "").toLowerCase() === ALLOWED_EMAIL &&
  user.emailVerified;

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: 24,
    boxSizing: "border-box",
    background: "#14192b",
    color: "#f4f5f8",
    fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    textAlign: "center",
  },
  title: { margin: 0, fontSize: "clamp(1.6rem, 5vw, 2.4rem)", fontWeight: 700 },
  text: { margin: 0, maxWidth: 420, opacity: 0.75, lineHeight: 1.5 },
  form: { display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 320, marginTop: 8 },
  input: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #3a4160",
    background: "#1d2440",
    color: "#f4f5f8",
    fontSize: 15,
  },
  button: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "none",
    background: "#4f7cff",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  secondary: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #3a4160",
    background: "transparent",
    color: "#f4f5f8",
    fontSize: 15,
    cursor: "pointer",
  },
  link: {
    marginTop: 24,
    background: "none",
    border: "none",
    color: "#f4f5f8",
    opacity: 0.45,
    fontSize: 13,
    cursor: "pointer",
    textDecoration: "underline",
  },
  message: { margin: 0, fontSize: 14, color: "#ffcf66", maxWidth: 320 },
};

export default function AccessGate({ children }) {
  const [user, setUser] = useState(undefined);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(
    () =>
      onAuthStateChanged(auth, async (current) => {
        if (current && !isAllowed(current)) {
          // Una cuenta autorizada sin verificar puede esperar la verificación;
          // cualquier otra cuenta se cierra.
          const ownEmail = (current.email || "").toLowerCase() === ALLOWED_EMAIL;
          if (!ownEmail) {
            await signOut(auth);
            setUser(null);
            return;
          }
        }
        setUser(current);
      }),
    []
  );

  if (user === undefined) return null;
  if (isAllowed(user)) return children;

  const checkEmail = () => {
    if (email.trim().toLowerCase() !== ALLOWED_EMAIL) {
      setMessage("Plataforma en construcción. El acceso todavía no está habilitado.");
      return false;
    }
    return true;
  };

  const run = async (action) => {
    setMessage("");
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setMessage(err && err.code ? `No se pudo continuar (${err.code}).` : "No se pudo continuar.");
    } finally {
      setBusy(false);
    }
  };

  const login = (e) => {
    e.preventDefault();
    if (!checkEmail()) return;
    run(async () => {
      const { user: signed } = await signInWithEmailAndPassword(auth, email.trim(), password);
      if (!signed.emailVerified) {
        await sendEmailVerification(signed);
        setMessage("Verificá tu email con el enlace que te enviamos y volvé a ingresar.");
      }
    });
  };

  const register = () => {
    if (!checkEmail()) return;
    run(async () => {
      const { user: created } = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await sendEmailVerification(created);
      setMessage("Cuenta creada. Verificá tu email con el enlace que te enviamos y volvé a ingresar.");
    });
  };

  const loginWithGoogle = () =>
    run(async () => {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      if (isAllowed(result.user)) return;
      // Cuenta no autorizada: si se acaba de crear, se elimina; en todo caso se cierra la sesión.
      if (getAdditionalUserInfo(result)?.isNewUser) {
        await result.user.delete().catch(() => {});
      }
      await signOut(auth);
      setMessage("Plataforma en construcción. El acceso todavía no está habilitado.");
    });

  const pendingVerification = user && !user.emailVerified;

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Plataforma en construcción</h1>
      <p style={styles.text}>Estamos trabajando en esta plataforma. Muy pronto vas a poder usarla.</p>

      {pendingVerification && (
        <>
          <p style={styles.message}>
            Verificá tu email ({user.email}) con el enlace que te enviamos y después tocá "Ya verifiqué".
          </p>
          <div style={styles.form}>
            <button
              style={styles.button}
              disabled={busy}
              onClick={() => run(async () => { await user.reload(); window.location.reload(); })}
            >
              Ya verifiqué
            </button>
            <button style={styles.secondary} disabled={busy} onClick={() => run(() => sendEmailVerification(user))}>
              Reenviar email
            </button>
            <button style={styles.secondary} disabled={busy} onClick={() => run(() => signOut(auth))}>
              Salir
            </button>
          </div>
        </>
      )}

      {!pendingVerification && !showLogin && (
        <button style={styles.link} onClick={() => setShowLogin(true)}>
          Acceso administrador
        </button>
      )}

      {!pendingVerification && showLogin && (
        <form style={styles.form} onSubmit={login}>
          <input
            style={styles.input}
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            style={styles.input}
            type="password"
            placeholder="Contraseña"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />
          <button style={styles.button} type="submit" disabled={busy}>
            Iniciar sesión
          </button>
          <button style={styles.secondary} type="button" disabled={busy} onClick={register}>
            Registrarse
          </button>
          <button style={styles.secondary} type="button" disabled={busy} onClick={loginWithGoogle}>
            Continuar con Google
          </button>
        </form>
      )}

      {message && <p style={styles.message}>{message}</p>}
    </div>
  );
}
